import React, { useState, useRef, useEffect } from 'react';
import { MAX_NAME_LENGTH, MAX_DESCRIPTION_LENGTH, MAX_INSTRUCTIONS_LENGTH } from './types';
import modelProviderMapping from '../../../../../../Utils/IdMappings/modelProviderMapping';
import { isModelPremium } from '../../../../../../Utils/misc/modelInsightUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAppStore } from '../../../../../../GlobalStore/GlobalStore';


interface ModeFormFieldsProps {
    name: string;
    description: string;
    instructions: string;
    preferredModel?: string;
    onNameChange: (name: string) => void;
    onDescriptionChange: (description: string) => void;
    onInstructionsChange: (instructions: string) => void;
    onPreferredModelChange: (model: string) => void;
}
const ModeFormFields: React.FC<ModeFormFieldsProps> = ({
    name,
    description,
    instructions,
    preferredModel = '',
    onNameChange,
    onDescriptionChange,
    onInstructionsChange,
    onPreferredModelChange
}) => {
    const userObj = useAppStore((state: any) => state.userObj);
    const modelOptions = ['Automatic', ...Object.keys(modelProviderMapping)];
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleModelSelect = (model: string) => {
        onPreferredModelChange(model === 'Automatic' ? '' : model);
        setIsDropdownOpen(false);
    };

    const displayValue = preferredModel || 'Automatic';
    const isModelInvalid = preferredModel && !modelProviderMapping[preferredModel];
    return (
        <>
            <div className="mb-3">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Mode Name*
                </label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value.slice(0, MAX_NAME_LENGTH))}
                    className="w-full bg-neutral-100 dark:bg-neutral-600 text-neutral-800 dark:text-neutral-200 rounded-md px-2 py-1 text-sm"
                    placeholder="Enter mode name"
                    maxLength={MAX_NAME_LENGTH}
                />
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {name.length}/{MAX_NAME_LENGTH}
                </div>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Description (optional)
                </label>
                <textarea
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
                    className="w-full bg-neutral-100 dark:bg-neutral-600 text-neutral-800 dark:text-neutral-200 rounded-md px-2 py-1 text-sm"
                    placeholder="Brief description of this mode"
                    rows={2}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                />
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {description.length}/{MAX_DESCRIPTION_LENGTH}
                </div>
            </div>
            <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Instructions*
                </label>
                <textarea
                    value={instructions}
                    onChange={(e) => onInstructionsChange(e.target.value.slice(0, MAX_INSTRUCTIONS_LENGTH))}
                    className="w-full bg-neutral-100 dark:bg-neutral-600 text-neutral-800 dark:text-neutral-200 rounded-md px-2 py-1 text-sm"
                    placeholder="Detailed instructions for this mode"
                    rows={4}
                    maxLength={MAX_INSTRUCTIONS_LENGTH}
                />
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {instructions.length}/{MAX_INSTRUCTIONS_LENGTH}
                </div>
            </div>
            <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Preferred Model (optional)
                </label>
                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="w-full bg-neutral-100 dark:bg-neutral-600 text-neutral-800 dark:text-neutral-200 rounded-md px-2 py-1 text-sm flex justify-between items-center hover:bg-neutral-300 dark:hover:bg-neutral-500 transition-colors duration-200"
                    >
                        <span className="flex items-center">
                            {displayValue} {isModelPremium(displayValue) && displayValue !== 'Automatic' ? (
                                <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">(Premium)</span>
                            ) : ''}
                        </span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    {isDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-neutral-100 dark:bg-neutral-600 border border-neutral-300 dark:border-neutral-500 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {modelOptions
                                .map((model) => (
                                <div
                                    key={model}
                                    className="px-2 py-2 text-sm hover:bg-neutral-300 dark:hover:bg-neutral-500 cursor-pointer flex justify-between items-center"
                                    onClick={() => handleModelSelect(model)}
                                >
                                    <span>{model}</span>
                                    {isModelPremium(model) && model !== 'Automatic' && (
                                        <span className="text-xs text-amber-600 dark:text-amber-400">(Premium)</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {isModelInvalid ? (
                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                        This model is no longer available and will use Automatic selection
                    </div>
                ) : preferredModel && isModelPremium(preferredModel) && !userObj.hasUsageBasedEnabled && (
                    <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                        Premium models require usage-based pricing to be enabled
                    </div>
                )}
            </div>
        </>
    );
};

export default ModeFormFields;